import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { REVENUE_BASE_OPTIONS, RevenueBaseTooltip } from '../constants/revenueBase';
import V3DealBracketsEditor from './V3DealBracketsEditor';
import useV3OfferPool from '../hooks/useV3OfferPool';
import { numOrNull } from '../utils/buildPoolEngineInput';
import { grossPercentToPoolPercent } from '../utils/poolShare';
import { describeSalesReached, formatSharePercent } from '../../../shared/poolShareOfGross';

const _ = require('lodash');

const OR = 'OR';
const AND = 'AND';

// O-2783 "pool over offers". Pool-mode editor for the POOL's shared deal: the
// door deal (guarantee / % / operator), revenue base, breakeven, payout cap /
// floor, and the shared budget's deal brackets (reused V3DealBracketsEditor).
//
// Persists to the pool via PATCH /teams/:id/offer_pools/v3/:id. Free-text inputs
// optimistically update local state and debounce-PATCH; discrete toggles /
// selects persist immediately. Mirrors the single-offer deal-terms input styles
// (deal-terms-form__* for the guarantee row, deal-terms-field for the rest) so
// it reads as the same form as a single-offer deal.
//
// `dollar_amount` → guarantee, `upside_percentage` → %, `pool_payout_cap` /
// `pool_payout_floor` map to the pool's clamp columns.
const V3PoolDealSection = ({ pool, team, poolSharePercent, dealBracketReached }) => {
  const { updatePoolDeal } = useV3OfferPool();
  const currencyLabel = team?.base_currency_symbol || 'USD';

  // O-4755 converter. Purely a calculator — it reads the pool's effective share
  // of gross and never writes to the pool or to any member, so it holds its own
  // input state and takes no part in the debounced save above.
  const [grossPercent, setGrossPercent] = useState('');
  const convertedPoolPercent = grossPercentToPoolPercent(grossPercent, poolSharePercent);

  // Local shadow of the pool's deal fields so keystrokes render immediately
  // before the debounced PATCH lands. Re-seed on pool IDENTITY only (mirroring
  // the single-offer pane): depending on the server-derived VALUE scalars made a
  // debounced PATCH's echo (which always returns a new pool object) re-fire this
  // effect and clobber the in-flight shadow mid-type. The enterPool toggle still
  // re-seeds because it flips pool?.id from null to the new id.
  const [local, setLocal] = useState(pool || {});
  useEffect(() => {
    setLocal(pool || {});
  }, [pool?.id]);

  // One debounced PATCH that accumulates field deltas so rapid edits across
  // fields coalesce into a single request without dropping any delta.
  const pendingRef = useRef({});
  const debouncedSave = useMemo(
    () => _.debounce(() => {
      const attrs = pendingRef.current;
      pendingRef.current = {};
      if (Object.keys(attrs).length > 0) updatePoolDeal(attrs);
    }, 600),
    [updatePoolDeal],
  );
  useEffect(() => () => debouncedSave.cancel(), [debouncedSave]);

  // Optimistic local update (instant input feedback) + debounced persist
  // (free-text). The preview recomputes when updatePoolDeal's PATCH round-trips
  // and applyPool folds the saved pool into context — the same "update after the
  // server confirms" path the budget line items use.
  const onEdit = (field, value) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
    pendingRef.current = { ...pendingRef.current, [field]: value };
    debouncedSave();
  };

  // Optimistic local update + immediate persist (discrete toggles / selects).
  const onEditNow = (field, value) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
    updatePoolDeal({ [field]: value });
  };

  const operator = local.dollar_or_percentage_operator || OR;
  // Default-true (matches the DB default) so an unset value behaves like the
  // checkbox's checked state — keeps the checkbox and the brackets visibility
  // in agreement.
  const fromDollarOne = local.from_dollar_one ?? true;

  return (
    <section className="deal-terms-card-v2 pool-deal-section" data-testid="v3-pool-deal-section">
      <div className="deal-terms-card-v2__title-block" style={{ padding: '12px 16px 0' }}>
        <span className="deal-terms-card-v2__title">Pool deal</span>
        <span className="deal-terms-card-v2__subtitle">
          The shared door deal that produces the pool the lineup splits.
        </span>
      </div>

      <div className="deal-terms-form" style={{ padding: '12px 16px' }}>
        {/* Guarantee */}
        <label className="deal-terms-form__label deal-terms-form__field--guarantee">
          Guarantee
          <div className="deal-terms-form__currency">
            <span className="deal-terms-form__currency-prefix">$</span>
            <input
              type="number"
              step="0.01"
              className="deal-terms-form__input deal-terms-form__input--right"
              data-testid="pool-deal-guarantee"
              value={local.dollar_amount ?? ''}
              onChange={(e) => onEdit('dollar_amount', numOrNull(e.target.value))}
            />
            <span className="deal-terms-form__currency-suffix">{currencyLabel}</span>
          </div>
        </label>

        {/* +/vs operator */}
        <div className="deal-terms-form__label deal-terms-form__field--operator">
          <span className="deal-terms-form__label-spacer" aria-hidden="true">&nbsp;</span>
          <div className="deal-terms-form__operator-group" role="radiogroup" aria-label="Pool guarantee combiner">
            <button
              type="button"
              className={`deal-terms-form__operator-btn ${operator === AND ? 'deal-terms-form__operator-btn--active' : ''}`}
              onClick={() => onEditNow('dollar_or_percentage_operator', AND)}
              aria-pressed={operator === AND}
              aria-label="Guarantee plus percent"
            >
              +
            </button>
            <button
              type="button"
              className={`deal-terms-form__operator-btn ${operator === OR ? 'deal-terms-form__operator-btn--active' : ''}`}
              onClick={() => onEditNow('dollar_or_percentage_operator', OR)}
              aria-pressed={operator === OR}
              aria-label="Guarantee versus percent"
            >
              vs
            </button>
          </div>
        </div>

        {/* Percentage */}
        <label className="deal-terms-form__label deal-terms-form__field--percent">
          % of ticket sales
          <div className="deal-terms-form__percent">
            <input
              type="number"
              step="0.01"
              className="deal-terms-form__input"
              data-testid="pool-deal-percentage"
              value={local.upside_percentage ?? ''}
              onChange={(e) => onEdit('upside_percentage', numOrNull(e.target.value))}
            />
            <span className="deal-terms-form__percent-suffix">%</span>
          </div>
        </label>
      </div>

      {/* Revenue base / breakeven / cap / floor — reuse the deal-terms-field
        * primitives so the controls match the single-offer deal terms. */}
      <div className="deal-terms-grid" style={{ padding: '0 16px 12px' }}>
        <label className="deal-terms-field">
          <span className="deal-terms-field__label">Basis<RevenueBaseTooltip /></span>
          <select
            className="deal-terms-field__select"
            data-testid="pool-deal-revenue-base"
            value={local.revenue_base || 'default'}
            onChange={(e) => onEditNow('revenue_base', e.target.value)}
          >
            {REVENUE_BASE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <label className="deal-terms-field">
          <span className="deal-terms-field__label">Breakeven threshold</span>
          <span className="deal-terms-field__input-wrap">
            <span className="deal-terms-field__prefix">$</span>
            <input
              type="number"
              step="0.01"
              className="deal-terms-field__input"
              data-testid="pool-deal-breakeven"
              value={local.breakeven_threshold ?? ''}
              onChange={(e) => onEdit('breakeven_threshold', numOrNull(e.target.value))}
            />
          </span>
        </label>

        <label className="deal-terms-field">
          <span className="deal-terms-field__label">Payout cap</span>
          <span className="deal-terms-field__input-wrap">
            <span className="deal-terms-field__prefix">$</span>
            <input
              type="number"
              step="0.01"
              className="deal-terms-field__input"
              data-testid="pool-deal-cap"
              value={local.pool_payout_cap ?? ''}
              onChange={(e) => onEdit('pool_payout_cap', numOrNull(e.target.value))}
            />
          </span>
        </label>

        <label className="deal-terms-field">
          <span className="deal-terms-field__label">Payout floor</span>
          <span className="deal-terms-field__input-wrap">
            <span className="deal-terms-field__prefix">$</span>
            <input
              type="number"
              step="0.01"
              className="deal-terms-field__input"
              data-testid="pool-deal-floor"
              value={local.pool_payout_floor ?? ''}
              onChange={(e) => onEdit('pool_payout_floor', numOrNull(e.target.value))}
            />
          </span>
        </label>
      </div>

      {/* O-4755. Deals arrive written as a share of GROSS ("the opener gets 10%
        * of the door") while a member's share is of the POOL, so bookers were
        * converting by hand. Read-only on purpose: writing the result into a
        * member's share would freeze a number that silently stops meaning "10%
        * of gross" the moment the pool deal changes. */}
      <div style={{ padding: '0 16px 12px' }}>
        <span className="deal-terms-field__label">Gross to pool</span>
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <span className="deal-terms-field__input-wrap" style={{ maxWidth: '140px' }}>
            <input
              type="number"
              step="0.01"
              className="deal-terms-field__input"
              min="0"
              data-testid="pool-gross-converter-input"
              aria-label="Share of gross to convert"
              placeholder="10"
              value={grossPercent}
              onChange={(e) => setGrossPercent(e.target.value)}
            />
            <span className="deal-terms-field__suffix">% of gross</span>
          </span>

          <span
            data-testid="pool-gross-converter-result"
            style={{ color: 'var(--od-c-6b7280, #6B7280)', fontSize: '13px' }}
          >
            {poolSharePercent === null && 'Set the pool deal to convert'}
            {poolSharePercent !== null && convertedPoolPercent !== null && (
              <>
                {`= ${formatSharePercent(convertedPoolPercent)} of pool ${describeSalesReached({ dealBracketReached })}`}
                {convertedPoolPercent > 100 && ' — exceeds the whole pool'}
              </>
            )}
          </span>
        </div>
        {!fromDollarOne && convertedPoolPercent !== null && (
          <div
            data-testid="pool-gross-converter-tiers"
            style={{ marginTop: '6px', color: 'var(--od-c-6b7280, #6B7280)', fontSize: '13px' }}
          >
            On a tiered pool, a share of the pool drifts once sales cross a tier. For a share of gross that
            holds, set the artist to Percentage of gross; a payout cap or floor on the artist still applies.
          </div>
        )}
      </div>

      {/* Shared budget deal brackets — same editor the single offer uses.
        * The engine applies the brackets only when the deal is NOT "from dollar
        * one" (FinancialSummaryMath gates them on !from_dollar_one), so the
        * toggle below is what turns the brackets on. Mirrors the V2 budget
        * "From Dollar One" checkbox; in V3 the flag lives on the pool (the
        * engine reads the pool's from_dollar_one for pool math). */}
      <div style={{ padding: '0 16px 12px' }}>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '8px' }}
        >
          <input
            type="checkbox"
            data-testid="pool-deal-from-dollar-one"
            checked={fromDollarOne}
            onChange={(e) => onEditNow('from_dollar_one', e.target.checked)}
          />
          <span className="deal-terms-field__label" style={{ marginBottom: 0 }}>From dollar one</span>
          <span style={{ color: 'var(--od-c-6b7280, #6B7280)', fontSize: '13px' }}>
            {fromDollarOne ? '(uncheck to use deal brackets)' : '(deal brackets active)'}
          </span>
        </label>

        {/* Brackets only show when enabled (from_dollar_one unchecked); when the
          * deal pays from dollar one the brackets are inert, so they're hidden. */}
        {!fromDollarOne && (
          <>
            <span className="deal-terms-field__label" style={{ display: 'block', marginBottom: '8px' }}>
              Deal brackets
            </span>
            <V3DealBracketsEditor />
          </>
        )}
      </div>
    </section>
  );
};

V3PoolDealSection.propTypes = {
  pool: PropTypes.object,
  team: PropTypes.object,
  // The pool's effective share of gross, as a percentage. Null until the engine
  // has produced both a gross and a pool to divide.
  poolSharePercent: PropTypes.number,
  dealBracketReached: PropTypes.shape({ number: PropTypes.number, bracketCount: PropTypes.number }),
};

V3PoolDealSection.defaultProps = {
  pool: null,
  team: null,
  poolSharePercent: null,
  dealBracketReached: null,
};

export default V3PoolDealSection;
